export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: "/v1/auth/login",
    REGISTER: "/v1/auth/register",
    VERIFY_EMAIL: "/v1/auth/verify-email",
    FORGOT_PASSWORD: "/v1/auth/forgot-password",
    RESET_PASSWORD: "/v1/auth/reset-password",
    ME: "/v1/auth/me",
    LOGOUT: "/v1/auth/logout",
  },

  // User Management
  USERS: {
    BASE: "/v1/users",
    BY_ID: (id: string) => `/v1/users/${id}`,
    STATUS: (id: string) => `/v1/users/${id}/status`,
    PROMOTE: (id: string) => `/v1/users/${id}/promote`,
    PAYMENT: (id: string) => `/v1/users/${id}/payment`,
    NOMINEE: (id: string) => `/v1/users/${id}/nominee`,
    MY_NOMINEE: "/v1/users/me/nominee",
    RESET_PASSWORD: (id: string) => `/v1/users/${id}/reset-password`,
    CHANGE_PASSWORD: `/v1/users/me/change-password`,
    APPROVE_AADHAR: (id: string) => `/v1/users/${id}/approve-aadhar`,
    VALIDATE_REFERRAL: (referralCode: string) =>
      `/v1/users/referral/${referralCode}`,
  },

  // Addresses
  ADDRESSES: {
    BASE: "/v1/addresses",
    BY_ID: (id: string) => `/v1/addresses/${id}`,
  },

  // Analytics (No v1 prefix)
  ANALYTICS: {
    USER_GROWTH: "/analytics/user-growth",
    COMMISSION_STATS: "/analytics/commission-stats",
    DONATION_SUMMARY: "/analytics/donation-summary",
    // V1 Analytics
    V1: {
      DASHBOARD: "/v1/analytics/dashboard",
      ADMIN: "/v1/analytics/admin",
      COMMISSIONS: "/v1/analytics/commissions",
      TEAM: "/v1/analytics/team",
      OVERVIEW: "/v1/analytics/overview",
    },
  },

  // Dashboard (No v1 prefix)
  DASHBOARD: {
    // Admin Dashboard
    ADMIN_STATS: "/v1/admin/dashboard/stats",
    ADMIN_GROWTH_TRENDS: "/v1/admin/dashboard/charts/growth-trends",
    ADMIN_REVENUE_BREAKDOWN: "/v1/admin/dashboard/charts/revenue-breakdown",
    ADMIN_PRODUCT_PERFORMANCE: "/v1/admin/dashboard/charts/product-performance",
    ADMIN_TOP_PERFORMERS: "/v1/admin/dashboard/top-performers",

    // Member Dashboard
    MEMBER_STATS: "/v1/member/dashboard/stats",
    MEMBER_COMMISSION_TRENDS: "/v1/member/dashboard/charts/commission-trends",
    MEMBER_TEAM_GROWTH: "/v1/member/dashboard/charts/team-growth",

    // Legacy endpoints (keep for backward compatibility)
    USER_STATS: "/user/dashboard/stats",
    COMMISSION_OVERVIEW: "/dashboard/commission-overview",
    RECENT_ACTIVITY: "/dashboard/recent-activity",
    NOTIFICATIONS: "/dashboard/notifications",
    NOTIFICATION_READ: (id: string) => `/dashboard/notifications/${id}/read`,
    METRICS: "/dashboard/metrics",
  },

  // Commissions
  COMMISSIONS: {
    BASE: "/v1/commissions",
    ALL: "/v1/commissions/all",
    MY: "/v1/commissions/my",
    TEAM: "/v1/commissions/team",
    USER: (userId: string) => `/v1/commissions/user/${userId}`,
    USER_TOTAL: (userId: string) => `/v1/commissions/user-total/${userId}`,
    USERS_SUMMARY: "/v1/commissions/users-summary",
    TEAM_BY_USER: (userId: string) => `/v1/commissions/team/${userId}`,
    ANALYTICS: "/v1/commissions/analytics",
    OVERVIEW: "/v1/commissions/overview",
    TOP_EARNERS: "/v1/commissions/top-earners",
    EXPORT: "/v1/commissions/export",
    STATUS: (id: string) => `/v1/commissions/${id}/status`,
    BULK_STATUS: "/v1/commissions/bulk-status",
    PAY: "/v1/commissions/pay",
    PAYMENT_HISTORY: "/v1/commissions/payment-history",
  },

  // Orders
  ORDERS: {
    BASE: "/v1/orders",
    BY_ID: (id: string) => `/v1/orders/${id}`,
    DELETE: (id: string) => `/v1/orders/${id}/delete`,
    CANCEL: (id: string) => `/v1/orders/${id}/cancel`,
    REFUND_APPROVE: (id: string) => `/v1/orders/${id}/refund/approve`,
    REFUND_REJECT: (id: string) => `/v1/orders/${id}/refund/reject`,
    STATUS: (id: string) => `/v1/orders/${id}/status`,
    ADMIN_ALL: "/v1/orders/admin/all",
    ADMIN_DELETED: "/v1/orders/admin/deleted",
    NORMAL: "/v1/orders/normal",
    REGISTRATION: "/v1/orders/registration",
    // Phase 2: Shipping estimation
    ESTIMATE_SHIPPING: "/v1/orders/estimate-shipping",
  },

  // Products (V1 API)
  PRODUCTS: {
    BASE: "/v1/products",
    BY_ID: (id: string) => `/v1/products/${id}`,
  },

  // Categories
  CATEGORIES: {
    BASE: "/v1/categories",
    BY_ID: (id: string) => `/v1/categories/${id}`,
    BY_SLUG: (slug: string) => `/v1/categories/slug/${slug}`,
    ACTIVE: "/v1/categories/active",
    DROPDOWN: "/v1/categories/dropdown",
    REORDER: "/v1/categories/reorder",
    HARD_DELETE: (id: string) => `/v1/categories/${id}/hard`,
  },

  // Shipping Zones
  SHIPPING: {
    ZONES: "/v1/shipping/zones",
    ZONE_BY_ID: (id: string) => `/v1/shipping/zones/${id}`,
    CALCULATE: "/v1/shipping/calculate",
    TEMPLATES: "/v1/shipping/templates",
  },

  // Storage / Media Uploads
  STORAGE: {
    UPLOAD: "/v1/storage/upload",
    TEMP_UPLOAD: "/v1/storage/uploads/temp",
    COMMIT: (id: string) => `/v1/storage/uploads/temp/${id}/commit`,
    DELETE: (key: string) => `/v1/storage/object/${key}`,
  },

  // Products Enhanced (Different API version - no v1 prefix)
  PRODUCTS_ENHANCED: {
    BASE: "/products",
    BY_ID: (id: string) => `/products/${id}`,
    STATS: "/products/stats",
    CATEGORIES: "/products/categories",
    BULK_OPERATION: "/products/bulk-operation",
    UPLOAD_IMAGE: "/products/upload-image",
    DUPLICATE: (id: string) => `/products/${id}/duplicate`,
    BY_CATEGORY: (category: string) => `/products/category/${category}`,
    SEARCH: "/products/search",
  },

  // Payments
  PAYMENTS: {
    BASE: "/v1/payments",
    BY_ID: (id: string) => `/v1/payments/${id}`,
    STATUS: (id: string) => `/v1/payments/${id}/status`,
  },

  // Wallet
  WALLET: {
    SUMMARY: "/v1/wallet",
    SUMMARY_WITH_COMMISSIONS: "/v1/wallet/summary-with-commissions",
    ALLOCATE: "/v1/wallet/allocate",
    STATEMENT: "/v1/wallet/statement",
    STATEMENT_BY_ORDER: "/v1/wallet/statement-by-order",
    ADMIN_USER: (userId: string) => `/v1/admin/wallets/${userId}`,
    ADMIN_LIST: "/v1/admin/wallets",
  },

  // Donations (No v1 prefix)
  DONATIONS: {
    BASE: "/donation",
    MY: "/donation/me",
    STATUS: (id: string) => `/donation/${id}/status`,
    // V1 Donations
    V1: {
      BASE: "/v1/donations",
      ADMIN: "/v1/donations/admin",
    },
  },

  // Emergency Withdrawals (No v1 prefix)
  EMERGENCY_WITHDRAWALS: {
    BASE: "/emergency-withdrawal",
    MY: "/emergency-withdrawal/me",
    PROCESS: (id: string) => `/emergency-withdrawal/${id}/process`,
    // V1 Emergency Withdrawals
    V1: {
      BASE: "/v1/emergency-withdrawals",
      ADMIN: "/v1/emergency-withdrawals/admin",
    },
  },

  // Referrals Admin
  REFERRALS_ADMIN: {
    CONFIG: "/v1/admin/referrals/config",
    ANALYTICS: {
      COMMISSION_DISTRIBUTION:
        "/v1/admin/referrals/analytics/commission-distribution",
      TOP_EARNERS: "/v1/admin/referrals/analytics/top-earners",
      WITHDRAWAL_TRENDS: "/v1/admin/referrals/analytics/withdrawal-trends",
      OVERVIEW: "/v1/admin/referrals/analytics/overview",
    },
    WITHDRAWALS: "/v1/admin/referrals/withdrawals",
    PROCESS_WITHDRAWAL: (id: string) =>
      `/v1/admin/referrals/withdrawals/${id}/process`,
    USER_NETWORK: (userId: string) =>
      `/v1/admin/referrals/users/${userId}/network`,
    USER_EARNINGS: (userId: string) =>
      `/v1/admin/referrals/users/${userId}/earnings`,
  },

  // Referrals (No v1 prefix)
  REFERRALS: "/referrals",

  // Contacts
  CONTACTS: {
    BASE: "/v1/contacts",
    BY_ID: (id: string) => `/v1/contacts/${id}`,
    APPROVE_PHONE: (id: string) => `/v1/contacts/${id}/approve-phone`,
  },

  // System/Admin
  LOGS: "/v1/logs",
  SYSTEM_CONFIG: {
    PAYMENT_QR: "/v1/system-config/payment-qr",
    ADMIN: {
      PAYMENT_QR: "/v1/admin/system-config/payment-qr",
    },
  },
  DISPATCH: {
    BASE: "/v1/dispatch",
    CREATE: "/v1/dispatch",
    BY_ORDER: "/v1/dispatch/order",
    BY_ID: (dispatchId: string) => `/v1/dispatch/${dispatchId}`,
    ALL: "/v1/dispatch",
    USER: (userId: string) => `/v1/dispatch/user/${userId}`,
    TRACK: "/v1/dispatch/tracking",
    UPDATE_STATUS: "/v1/dispatch/status",
    UPDATE: "/v1/dispatch",
    DELETE: "/v1/dispatch",
    STATS: "/v1/dispatch/stats",
    OVERDUE: "/v1/dispatch/overdue",
    PENDING_RETURNS: "/v1/dispatch/returns/pending",
    BULK_UPDATE: "/v1/dispatch/bulk/status-update",
    COURIERS: "/v1/dispatch/couriers",
    ANALYTICS: "/v1/dispatch/analytics",
    UPLOAD_PHOTOS: "/v1/dispatch/photos",
    EXPORT: "/v1/dispatch/export",
  },
  // Courier Partners
  COURIER_PARTNERS: {
    // Public endpoints (no auth required)
    PUBLIC: "/v1/system-config/courier-partners",
    OPTIONS: "/v1/system-config/courier-partners/options",

    // Admin endpoints (auth required)
    ADMIN: {
      BASE: "/v1/admin/system-config/couriers",
      BY_ID: (id: string) => `/v1/admin/system-config/couriers/${id}`,
      BULK_UPDATE: "/v1/admin/system-config/couriers/bulk-update",
      TEST_CONNECTION: (id: string) =>
        `/v1/admin/system-config/couriers/${id}/test-connection`,
      METRICS: (id: string) => `/v1/admin/system-config/couriers/${id}/metrics`,
    },
  },
  MANUAL_APPROVAL: {
    BASE: "/v1/approvals/manual",
    APPROVE: (id: string) => `/manual-approval/${id}/approve`,
    APPROVE_PHONE: (id: string) => `/v1/contacts/${id}/approve-phone`,
    APPROVE_AADHAR: (id: string) => `/v1/users/${id}/approve-aadhar`,
    APPROVE_PAYMENT: (id: string) => `/v1/users/${id}/approve-payment`,
  },

  // KYC
  KYC: {
    ME: "/v1/kyc/me",
    SUBMIT_PAN: "/v1/kyc/submit-pan",
    SUBMIT_BANK: "/v1/kyc/submit-bank",
    SUBMIT: "/v1/kyc/submit",
    ACCEPT_DISCLAIMER: "/v1/kyc/accept-disclaimer",
    ADMIN_LIST: "/v1/kyc/admin/list",
    ADMIN_BY_USER: (userId: string) => `/v1/kyc/admin/user/${userId}`,
    ADMIN_START_REVIEW: (id: string) => `/v1/kyc/admin/${id}/start-review`,
    ADMIN_APPROVE: "/v1/kyc/admin/approve",
    ADMIN_REJECT: "/v1/kyc/admin/reject",
  },

  // Returns / Refunds
  RETURNS: {
    CREATE: "/v1/returns",
    MY: "/v1/returns/my",
    BY_ID: (id: string) => `/v1/returns/${id}`,
    ADMIN_LIST: "/v1/returns/admin/list",
    ADMIN_QC: (id: string) => `/v1/returns/admin/${id}/qc`,
    ADMIN_APPROVE: "/v1/returns/admin/approve",
    ADMIN_REJECT: "/v1/returns/admin/reject",
    ADMIN_REFUND: "/v1/returns/admin/refund",
  },

  // Invoices
  INVOICES: {
    MY: "/v1/invoices/my",
    BY_ID: (id: string) => `/v1/invoices/${id}`,
    BY_ORDER: (orderId: string) => `/v1/invoices/order/${orderId}`,
    ADMIN_LIST: "/v1/invoices/admin/list",
    GENERATE: "/v1/invoices/generate",
    BULK_GENERATE: "/v1/invoices/bulk-generate",
    RESEND_EMAIL: "/v1/invoices/resend-email",
    CANCEL: (id: string) => `/v1/invoices/${id}/cancel`,
    BACKFILL: "/v1/invoices/backfill",
    MY_BACKFILL: "/v1/invoices/my-backfill",
    PDF_URL: (id: string) => `/v1/invoices/${id}/pdf`,
  },

  // Tax Configuration (Admin)
  TAX_CONFIG: {
    GET: "/v1/tax-config",
    UPDATE: "/v1/tax-config",
  },

  // Commission Bonuses
  COMMISSION_BONUSES: {
    MY: "/v1/referrals/my-bonuses",
    EARNING_CAP: "/v1/referrals/earning-cap",
    ADMIN_LIST: "/v1/admin/referrals/bonuses",
    ADMIN_CREATE: "/v1/admin/referrals/bonuses",
    ADMIN_APPLY: "/v1/admin/referrals/bonuses/apply",
    ADMIN_SIMULATE: "/v1/admin/referrals/simulate",
  },

  // In-App Notifications
  NOTIFICATIONS: {
    MY: "/v1/notifications",
    MARK_READ: (id: string) => `/v1/notifications/${id}/read`,
    MARK_ALL_READ: "/v1/notifications/read-all",
    DELETE: (id: string) => `/v1/notifications/${id}`,
    ANNOUNCEMENTS: "/v1/notifications/announcements",
    ADMIN_CREATE: "/v1/notifications/admin/announcements",
    ADMIN_ANNOUNCEMENTS: "/v1/notifications/admin/announcements",
    ADMIN_PUBLISH: (id: string) =>
      `/v1/notifications/admin/announcements/${id}/publish`,
    ADMIN_UPDATE: (id: string) =>
      `/v1/notifications/admin/announcements/${id}`,
    ADMIN_DELETE: (id: string) =>
      `/v1/notifications/admin/announcements/${id}`,
  },

  // Support Tickets
  SUPPORT: {
    CREATE: "/v1/support/tickets",
    MY: "/v1/support/tickets",
    BY_ID: (id: string) => `/v1/support/tickets/${id}`,
    REPLY: (id: string) => `/v1/support/tickets/${id}/reply`,
    CLOSE: (id: string) => `/v1/support/tickets/${id}/close`,
    ADMIN_LIST: "/v1/support/admin/tickets",
    ADMIN_BY_ID: (id: string) => `/v1/support/admin/tickets/${id}`,
    ADMIN_REPLY: (id: string) => `/v1/support/admin/tickets/${id}/reply`,
    ADMIN_STATUS: (id: string) => `/v1/support/admin/tickets/${id}/status`,
    ADMIN_PRIORITY: (id: string) =>
      `/v1/support/admin/tickets/${id}/priority`,
    ADMIN_ASSIGN: (id: string) => `/v1/support/admin/tickets/${id}/assign`,
  },

  // Audit Logs (Admin)
  AUDIT_LOGS: {
    LIST: "/v1/audit-logs",
  },
} as const;
